/**
 * Debug Overlay for Chatbot Analytics Tracker
 * Provides visual debugging panel for tracking events in real-time
 */

export interface DebugEvent {
  id: string;
  type: string;
  timestamp: string;
  data: any;
  status: 'queued' | 'sent' | 'failed';
}

export class DebugOverlay {
  private container: HTMLDivElement | null = null;
  private eventsLog: HTMLDivElement | null = null;
  private statusBadge: HTMLButtonElement | null = null;
  private events: DebugEvent[] = [];
  private isVisible: boolean = false;
  private maxEvents: number = 100;

  constructor() {
    // Check if debug mode should be enabled via URL param
    if (this.shouldEnableDebug()) {
      this.init();
    }
  }

  /**
   * Check if debug mode should be enabled
   */
  private shouldEnableDebug(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has('chatbot_debug') || urlParams.get('chatbot_debug') === 'true';
  }

  /**
   * Initialize the debug overlay
   */
  init(): void {
    if (this.container) return;

    this.createStyles();
    this.createContainer();
    this.createToggleButton();
    
    
    // Add keyboard shortcut
    document.addEventListener('keydown', (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        this.toggle();
      }
    });
  }

  /**
   * Create CSS styles for the overlay
   */
  private createStyles(): void {
    const style = document.createElement('style');
    style.id = 'chatbot-debug-styles';
    style.textContent = `
      .chatbot-debug-container {
        position: fixed;
        bottom: 20px;
        right: 20px;
        width: 400px;
        max-height: 500px;
        background: #1a1a2e;
        border: 1px solid #4a4a6a;
        border-radius: 12px;
        box-shadow: 0 8px 32px rgba(0,0,0,0.4);
        font-family: 'Segoe UI', system-ui, sans-serif;
        font-size: 12px;
        color: #e0e0e0;
        z-index: 999999;
        display: none;
        flex-direction: column;
        overflow: hidden;
      }
      
      .chatbot-debug-container.visible {
        display: flex;
      }
      
      .chatbot-debug-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 16px;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        font-weight: 600;
        font-size: 13px;
      }
      
      .chatbot-debug-header-buttons {
        display: flex;
        gap: 8px;
      }
      
      .chatbot-debug-header button {
        background: rgba(255,255,255,0.2);
        border: none;
        color: white;
        padding: 4px 8px;
        border-radius: 4px;
        cursor: pointer;
        font-size: 11px;
      }
      
      .chatbot-debug-header button:hover {
        background: rgba(255,255,255,0.3);
      }
      
      .chatbot-debug-stats {
        display: flex;
        gap: 16px;
        padding: 10px 16px;
        background: #16213e;
        border-bottom: 1px solid #4a4a6a;
      }
      
      .chatbot-debug-stat {
        text-align: center;
      }
      
      .chatbot-debug-stat-value {
        font-size: 18px;
        font-weight: bold;
        color: #64ffda;
      }
      
      .chatbot-debug-stat-label {
        font-size: 10px;
        color: #888;
        text-transform: uppercase;
      }
      
      .chatbot-debug-session {
        padding: 8px 16px;
        background: #0f3460;
        border-bottom: 1px solid #4a4a6a;
        font-size: 11px;
      }
      
      .chatbot-debug-session-row {
        display: flex;
        justify-content: space-between;
        margin: 2px 0;
      }
      
      .chatbot-debug-session-label {
        color: #888;
      }
      
      .chatbot-debug-session-value {
        color: #64ffda;
        font-family: monospace;
      }
      
      .chatbot-debug-events {
        flex: 1;
        overflow-y: auto;
        max-height: 300px;
      }
      
      .chatbot-debug-event {
        padding: 10px 16px;
        border-bottom: 1px solid #2a2a4a;
        transition: background 0.2s;
      }
      
      .chatbot-debug-event:hover {
        background: #252545;
      }
      
      .chatbot-debug-event-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 4px;
      }
      
      .chatbot-debug-event-type {
        font-weight: 600;
        color: #ff6b6b;
      }
      
      .chatbot-debug-event-type.page_view { color: #4ecdc4; }
      .chatbot-debug-event-type.product_viewed { color: #f9ca24; }
      .chatbot-debug-event-type.add_to_cart { color: #6c5ce7; }
      .chatbot-debug-event-type.checkout_initiated { color: #fd79a8; }
      .chatbot-debug-event-type.purchase_completed { color: #00b894; }
      .chatbot-debug-event-type.chat_started { color: #0984e3; }
      .chatbot-debug-event-type.chat_message { color: #74b9ff; }
      
      .chatbot-debug-event-time {
        font-size: 10px;
        color: #666;
      }
      
      .chatbot-debug-event-status {
        display: inline-block;
        padding: 2px 6px;
        border-radius: 4px;
        font-size: 9px;
        font-weight: 600;
        text-transform: uppercase;
      }
      
      .chatbot-debug-event-status.queued { background: #f39c12; color: #000; }
      .chatbot-debug-event-status.sent { background: #27ae60; color: #fff; }
      .chatbot-debug-event-status.failed { background: #e74c3c; color: #fff; }
      
      .chatbot-debug-event-data {
        font-family: monospace;
        font-size: 10px;
        color: #888;
        white-space: pre-wrap;
        word-break: break-all;
        max-height: 60px;
        overflow: hidden;
      }
      
      .chatbot-debug-event.expanded .chatbot-debug-event-data {
        max-height: none;
      }
      
      .chatbot-debug-toggle {
        position: fixed;
        bottom: 20px;
        right: 20px;
        width: 50px;
        height: 50px;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        border: none;
        border-radius: 50%;
        color: white;
        font-size: 20px;
        cursor: pointer;
        box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
        z-index: 999998;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: transform 0.2s, box-shadow 0.2s;
      }
      
      .chatbot-debug-toggle:hover {
        transform: scale(1.1);
        box-shadow: 0 6px 20px rgba(102, 126, 234, 0.6);
      }
      
      .chatbot-debug-toggle.has-new {
        animation: pulse 1s infinite;
      }
      
      @keyframes pulse {
        0%, 100% { box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4); }
        50% { box-shadow: 0 4px 25px rgba(100, 255, 218, 0.8); }
      }
      
      .chatbot-debug-empty {
        padding: 40px 20px;
        text-align: center;
        color: #666;
      }
    `;
    // Ensure head exists before appending
    if (document.head) {
      document.head.appendChild(style);
    } else {
      document.addEventListener('DOMContentLoaded', () => {
        document.head.appendChild(style);
      });
    }
  }

  /**
   * Create the main container
   */
  private createContainer(): void {
    this.container = document.createElement('div');
    this.container.className = 'chatbot-debug-container';
    this.container.innerHTML = `
      <div class="chatbot-debug-header">
        <span>🔍 Chatbot Analytics Debug</span>
        <div class="chatbot-debug-header-buttons">
          <button onclick="ChatbotAnalytics._debug.exportEvents()">📥 Export</button>
          <button onclick="ChatbotAnalytics._debug.clearEvents()">🗑️ Clear</button>
          <button onclick="ChatbotAnalytics._debug.hide()">✕</button>
        </div>
      </div>
      <div class="chatbot-debug-stats">
        <div class="chatbot-debug-stat">
          <div class="chatbot-debug-stat-value" id="debug-stat-total">0</div>
          <div class="chatbot-debug-stat-label">Total</div>
        </div>
        <div class="chatbot-debug-stat">
          <div class="chatbot-debug-stat-value" id="debug-stat-queued">0</div>
          <div class="chatbot-debug-stat-label">Queued</div>
        </div>
        <div class="chatbot-debug-stat">
          <div class="chatbot-debug-stat-value" id="debug-stat-sent">0</div>
          <div class="chatbot-debug-stat-label">Sent</div>
        </div>
        <div class="chatbot-debug-stat">
          <div class="chatbot-debug-stat-value" id="debug-stat-failed">0</div>
          <div class="chatbot-debug-stat-label">Failed</div>
        </div>
      </div>
      <div class="chatbot-debug-session" id="debug-session-info">
        <div class="chatbot-debug-session-row">
          <span class="chatbot-debug-session-label">Session ID:</span>
          <span class="chatbot-debug-session-value" id="debug-session-id">-</span>
        </div>
        <div class="chatbot-debug-session-row">
          <span class="chatbot-debug-session-label">Visitor ID:</span>
          <span class="chatbot-debug-session-value" id="debug-visitor-id">-</span>
        </div>
        <div class="chatbot-debug-session-row">
          <span class="chatbot-debug-session-label">Chatbot Assisted:</span>
          <span class="chatbot-debug-session-value" id="debug-chatbot-assisted">No</span>
        </div>
      </div>
      <div class="chatbot-debug-events" id="debug-events-log">
        <div class="chatbot-debug-empty">No events tracked yet</div>
      </div>
    `;
    
    // Ensure body exists before appending
    if (document.body) {
      document.body.appendChild(this.container);
      this.eventsLog = this.container.querySelector('#debug-events-log');
    } else {
      // Wait for DOM to be ready
      document.addEventListener('DOMContentLoaded', () => {
        if (this.container) {
          document.body.appendChild(this.container);
          this.eventsLog = this.container.querySelector('#debug-events-log');
        }
      });
    }
  }

  /**
   * Create toggle button
   */
  private createToggleButton(): void {
    const button = document.createElement('button');
    button.className = 'chatbot-debug-toggle';
    button.innerHTML = '🔍';
    button.title = 'Toggle Debug Panel (Ctrl+Shift+D)';
    button.onclick = () => this.toggle();
    this.statusBadge = button;
    
    // Ensure body exists before appending
    if (document.body) {
      document.body.appendChild(button);
    } else {
      document.addEventListener('DOMContentLoaded', () => {
        document.body.appendChild(button);
      });
    }
  }

  /**
   * Toggle visibility
   */
  toggle(): void {
    this.isVisible = !this.isVisible;
    if (this.container) {
      this.container.classList.toggle('visible', this.isVisible);
    }
    if (this.statusBadge) {
      this.statusBadge.classList.remove('has-new');
    }
  }

  /**
   * Show the overlay
   */
  show(): void {
    this.isVisible = true;
    if (this.container) {
      this.container.classList.add('visible');
    }
  }

  /**
   * Hide the overlay
   */
  hide(): void {
    this.isVisible = false;
    if (this.container) {
      this.container.classList.remove('visible');
    }
  }

  /**
   * Log an event
   */
  logEvent(event: DebugEvent): void {
    this.events.unshift(event);
    
    // Trim to max events
    if (this.events.length > this.maxEvents) {
      this.events = this.events.slice(0, this.maxEvents);
    }
    
    this.renderEvents();
    this.updateStats();
    
    // Flash the toggle button if not visible
    if (!this.isVisible && this.statusBadge) {
      this.statusBadge.classList.add('has-new');
    }
  }

  /**
   * Update event status
   */
  updateEventStatus(eventId: string, status: 'queued' | 'sent' | 'failed'): void {
    const event = this.events.find(e => e.id === eventId);
    if (event) {
      event.status = status;
      this.renderEvents();
      this.updateStats();
    }
  }

  /**
   * Update session info display
   */
  updateSessionInfo(session: any): void {
    const sessionIdEl = document.getElementById('debug-session-id');
    const visitorIdEl = document.getElementById('debug-visitor-id');
    const chatbotEl = document.getElementById('debug-chatbot-assisted');
    
    if (sessionIdEl && session?.sessionId) {
      sessionIdEl.textContent = session.sessionId.substring(0, 20) + '...';
    }
    if (visitorIdEl && session?.visitorId) {
      visitorIdEl.textContent = session.visitorId.substring(0, 20) + '...';
    }
    if (chatbotEl) {
      chatbotEl.textContent = session?.chatbotAssisted ? '✅ Yes' : '❌ No';
    }
  }

  /**
   * Render events list
   */
  private renderEvents(): void {
    if (!this.eventsLog) return;
    
    if (this.events.length === 0) {
      this.eventsLog.innerHTML = '<div class="chatbot-debug-empty">No events tracked yet</div>';
      return;
    }
    
    this.eventsLog.innerHTML = this.events.map(event => `
      <div class="chatbot-debug-event" onclick="this.classList.toggle('expanded')">
        <div class="chatbot-debug-event-header">
          <span class="chatbot-debug-event-type ${event.type}">${event.type}</span>
          <span class="chatbot-debug-event-status ${event.status}">${event.status}</span>
        </div>
        <div class="chatbot-debug-event-time">${event.timestamp}</div>
        <div class="chatbot-debug-event-data">${JSON.stringify(event.data, null, 2)}</div>
      </div>
    `).join('');
  }

  /**
   * Update statistics
   */
  private updateStats(): void {
    const totalEl = document.getElementById('debug-stat-total');
    const queuedEl = document.getElementById('debug-stat-queued');
    const sentEl = document.getElementById('debug-stat-sent');
    const failedEl = document.getElementById('debug-stat-failed');
    
    if (totalEl) totalEl.textContent = this.events.length.toString();
    if (queuedEl) queuedEl.textContent = this.events.filter(e => e.status === 'queued').length.toString();
    if (sentEl) sentEl.textContent = this.events.filter(e => e.status === 'sent').length.toString();
    if (failedEl) failedEl.textContent = this.events.filter(e => e.status === 'failed').length.toString();
  }

  /**
   * Export events to clipboard
   */
  exportEvents(): void {
    const data = {
      exportTime: new Date().toISOString(),
      events: this.events
    };
    
    const json = JSON.stringify(data, null, 2);
    
    if (navigator.clipboard) {
      navigator.clipboard.writeText(json).then(() => {
        alert('✅ Events exported to clipboard!');
      }).catch(() => {
        this.downloadAsFile(json);
      });
    } else {
      this.downloadAsFile(json);
    }
  }

  /**
   * Download as file fallback
   */
  private downloadAsFile(content: string): void {
    const blob = new Blob([content], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chatbot-debug-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  /**
   * Clear all events
   */
  clearEvents(): void {
    this.events = [];
    this.renderEvents();
    this.updateStats();
  }

  /**
   * Destroy the overlay
   */
  destroy(): void {
    if (this.container) {
      this.container.remove();
      this.container = null;
    }
    if (this.statusBadge) {
      this.statusBadge.remove();
      this.statusBadge = null;
    }
    const styles = document.getElementById('chatbot-debug-styles');
    if (styles) styles.remove();
  }
}
